'use client';

import { useEffect, useRef, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useCart,
  useCommerceSource,
  useBlockPart,
  tx,
  PortalLink,
  type AccountAddress,
  type CheckoutAddress,
  type CollectionPoint,
  type DecimalString,
} from '@akademi/site-builder-runtime';
import { addressLine, todaysHours } from './collectionHours';
import { addressSummary } from '../account/addressDraft';
import {
  AdapterPlaceholder,
  BlockRoot,
  formatDecimalMoney,
  sharedStyles,
} from '@akademi/site-builder-blocks';
import { rememberPlacedOrder } from './orderHandoff';
import type { ShopPaymentOptions, TransferCheckoutRequest } from './bankTransfer';

const { sectionInner } = sharedStyles;

type Localized = string | Record<string, string>;

/**
 * Checkout: who the shopper is, where it goes, how it gets there, and place.
 *
 * <h3>The delivery charge is in the total before they commit</h3>
 *
 * <p>Choosing a delivery method does not just record a radio — it calls
 * {@code setFulfilment}, which re-prices the basket server-side and returns
 * new totals. So the number beside "Total" is the number that will be charged.
 * Deferring the charge to the order confirmation is the oldest dark pattern in
 * retail and the reason this block re-totals on every change.
 *
 * <h3>One page, not a wizard</h3>
 *
 * <p>Address, method and summary are all visible at once. A multi-step
 * checkout exists to measure funnels, not to help anybody; every step is
 * another place to lose a shopper, and none of these fields depends on an
 * answer from a previous step.
 *
 * <h3>No account gate, deliberately</h3>
 *
 * <p>Forcing registration is the single largest cause of abandoned baskets in
 * every study anyone has run, and the backend treats a guest order as
 * first-class — it is looked up afterwards with the order number plus two
 * proofs the shopper already typed here. A shopper who IS signed in starts from
 * their own name, email and default address, can pick another saved address,
 * and can save a typed one for next time.
 *
 * <h3>Click & collect is offered only where it exists</h3>
 *
 * <p>The choice between delivery and collection appears only when the shop has
 * collection points. Choosing a store re-prices the basket as a collection — no
 * delivery charge — and the address fields become the shopper's own address,
 * whose postcode is then what finds the order again.
 *
 * <h3>Money is never computed here</h3>
 *
 * <p>Every figure is a string from the server. Nothing in this file adds two
 * amounts together: a total the browser worked out is a total that can
 * disagree with the one the shopper is charged.
 */
export interface CheckoutFormProps {
  title?: Localized;
  /** Where the shopper lands once the order is placed. */
  successHref?: string;
  /** Where "back to the shop" goes from an empty basket. */
  emptyHref?: string;
  /** Back to the basket to change something. */
  cartHref?: string;
  /** ISO 3166-1 alpha-2 the address form starts on. */
  defaultCountryCode?: string;
  /** A trade shop wants a purchase-order box; a consumer one does not. */
  showPoNumber?: boolean;
  /** "Anything we should know?" — delivery instructions, mostly. */
  showNote?: boolean;
  /** When set, a terms checkbox is required and links here. */
  termsHref?: string;
}

interface FormState {
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  line1: string;
  line2: string;
  locality: string;
  region: string;
  postcode: string;
  countryCode: string;
  customerPoNumber: string;
  note: string;
}

/** The fields a saved address fills; changing any of them makes it a typed address again. */
const ADDRESS_FIELDS: ReadonlyArray<keyof FormState> = ['line1', 'line2', 'locality', 'region', 'postcode', 'countryCode'];

function withAddress(form: FormState, saved: AccountAddress): FormState {
  return {
    ...form,
    line1: saved.line1 ?? '',
    line2: saved.line2 ?? '',
    locality: saved.locality ?? '',
    region: saved.region ?? '',
    postcode: saved.postcode ?? '',
    countryCode: saved.countryCode ?? form.countryCode,
    contactPhone: form.contactPhone || (saved.phone ?? ''),
  };
}

const EMPTY_FORM: Omit<FormState, 'countryCode'> = {
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  line1: '',
  line2: '',
  locality: '',
  region: '',
  postcode: '',
  customerPoNumber: '',
  note: '',
};

export function CheckoutForm({
  title,
  successHref = '/order-confirmation',
  emptyHref = '/categories',
  cartHref = '/cart',
  defaultCountryCode = 'GB',
  showPoNumber = false,
  showNote = true,
  termsHref,
}: CheckoutFormProps) {
  const cartState = useCart();
  const commerce = useCommerceSource();
  const part = useBlockPart();
  const { t, i18n } = useTranslation('site-builder-blocks');

  const [form, setForm] = useState<FormState>({
    ...EMPTY_FORM,
    countryCode: defaultCountryCode,
  });
  const [methodId, setMethodId] = useState<number | null>(null);
  const [placing, setPlacing] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<ShopPaymentOptions | null>(null);
  const [instrument, setInstrument] = useState<string | null>(null);
  const [payBy, setPayBy] = useState<'CARD' | 'BANK_TRANSFER'>('CARD');
  const [points, setPoints] = useState<CollectionPoint[]>([]);
  const [mode, setMode] = useState<'DELIVERY' | 'COLLECTION'>('DELIVERY');
  const [pointId, setPointId] = useState<number | null>(null);
  const [signedIn, setSignedIn] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState<AccountAddress[]>([]);
  /** The saved address the fields hold, until the shopper changes one of them. */
  const [pickedId, setPickedId] = useState<number | null>(null);
  const [saveToBook, setSaveToBook] = useState(false);
  const formRef = useRef(form);
  formRef.current = form;

  // A signed-in shopper starts from their own details and their default address.
  // Only empty fields are filled, so nothing typed before the answer arrives is lost.
  useEffect(() => {
    let cancelled = false;
    const source = commerce;
    if (!source?.accountProfile) return;
    void (async () => {
      const profile = await source.accountProfile!().catch(() => null);
      if (cancelled || !profile) return;
      setSignedIn(true);
      const book = source.accountAddresses ? await source.accountAddresses().catch(() => []) : [];
      if (cancelled) return;
      setSavedAddresses(book);
      const current = formRef.current;
      const name = [profile.firstName, profile.lastName].filter(Boolean).join(' ');
      let next: FormState = {
        ...current,
        contactEmail: current.contactEmail || profile.email,
        contactName: current.contactName || name,
      };
      const preferred = book.find((address) => address.defaultAddress);
      const untouched = ADDRESS_FIELDS.every((field) => field === 'countryCode' || !current[field]);
      if (preferred && untouched) {
        next = withAddress(next, preferred);
        setPickedId(preferred.id);
      }
      setForm(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  // Where collection is offered decides whether the choice is shown at all; a
  // shop with no collection points sees exactly the delivery checkout it had.
  useEffect(() => {
    let cancelled = false;
    if (!commerce?.collectionPoints) return;
    commerce
      .collectionPoints()
      .then((offered) => {
        if (!cancelled) setPoints(offered ?? []);
      })
      .catch(() => {
        // No list, no collection option: delivery still works.
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  // Asked for once, on mount. It decides whether there is a payment step at all,
  // so it cannot wait until the shopper presses the button.
  useEffect(() => {
    let cancelled = false;
    if (!commerce?.paymentOptions) return;
    commerce
      .paymentOptions()
      .then((options) => {
        if (cancelled || !options) return;
        setPaymentOptions(options);
        // Pre-select the first simulated outcome so the common case — a tester
        // wanting a successful order — is one click, not two.
        setInstrument(options.testInstruments?.[0]?.token ?? null);
      })
      .catch(() => {
        // A shop whose payment configuration cannot be read still takes orders.
        // Silence here means "no payment step", which is the safe reading.
      });
    return () => {
      cancelled = true;
    };
  }, [commerce]);

  if (!cartState || !cartState.available || !commerce?.checkout) {
    return (
      <BlockRoot as="section">
        <div style={sectionInner}>
          <AdapterPlaceholder
            blockType="CheckoutForm"
            adapterName="CommerceSource"
            reason={t('commerce.checkout.noAdapter')}
          />
        </div>
      </BlockRoot>
    );
  }

  const { cart, busy } = cartState;
  const locale = i18n.language || 'en';
  const money = (amount: DecimalString | undefined) =>
    formatDecimalMoney(amount, cart?.currency ?? '', locale);
  const lines = cart?.lines ?? [];
  const options = cart?.shippingOptions ?? [];

  if (lines.length === 0) {
    return (
      <BlockRoot as="section">
        <div style={{ ...sectionInner, maxWidth: 720 }}>
          <h1 style={titleStyle}>{tx(title) || t('commerce.checkout.title')}</h1>
          <p style={{ margin: '0 0 16px' }}>{t('commerce.checkout.empty')}</p>
          <PortalLink href={emptyHref} style={secondaryButtonStyle}>
            {t('commerce.checkout.backToShop')}
          </PortalLink>
        </div>
      </BlockRoot>
    );
  }

  const set = (field: keyof FormState) => (value: string) => {
    setForm((previous) => ({ ...previous, [field]: value }));
    if (ADDRESS_FIELDS.includes(field)) setPickedId(null);
  };

  const picked = savedAddresses.find((saved) => saved.id === pickedId) ?? null;

  /** A saved address fills the fields; "enter a new address" empties them for typing. */
  function pickSaved(value: string) {
    const saved = savedAddresses.find((address) => String(address.id) === value);
    if (saved) {
      setForm((previous) => withAddress(previous, saved));
      setPickedId(saved.id);
      setSaveToBook(false);
      return;
    }
    setPickedId(null);
    setForm((previous) => ({
      ...previous,
      line1: '',
      line2: '',
      locality: '',
      region: '',
      postcode: '',
      countryCode: defaultCountryCode,
    }));
  }

  const address = (): CheckoutAddress => ({
    // A saved address goes to the person it was saved for, who is not always the one paying.
    recipientName: picked?.recipientName || form.contactName.trim() || undefined,
    company: picked?.company || undefined,
    line1: form.line1.trim() || undefined,
    line2: form.line2.trim() || undefined,
    locality: form.locality.trim() || undefined,
    region: form.region.trim() || undefined,
    postcode: form.postcode.trim() || undefined,
    countryCode: form.countryCode.trim().toUpperCase() || undefined,
    phone: form.contactPhone.trim() || undefined,
  });

  /** Picking a method re-prices the basket, so the summary is never a guess. */
  async function chooseMethod(id: number) {
    setMethodId(id);
    setFailure(null);
    await cartState!.setFulfilment({ fulfilmentType: 'DELIVERY', shippingMethodId: id });
  }

  async function chooseMode(next: 'DELIVERY' | 'COLLECTION') {
    if (next === mode) return;
    setMode(next);
    setFailure(null);
    if (next === 'DELIVERY') {
      setPointId(null);
      // Back to delivery with no method yet: the basket drops the collection
      // and waits for a method to price the delivery again.
      await cartState!.setFulfilment({ fulfilmentType: 'DELIVERY' });
    } else {
      setMethodId(null);
      const only = points.length === 1 ? points[0] : undefined;
      if (only) await choosePoint(only.id);
    }
  }

  /** Choosing a store re-prices the basket as a collection: no delivery charge. */
  async function choosePoint(id: number) {
    setPointId(id);
    setFailure(null);
    await cartState!.setFulfilment({ fulfilmentType: 'COLLECTION', collectionLocationId: id });
  }

  const collecting = mode === 'COLLECTION';

  async function place(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (placing) return;
    if (collecting && pointId == null) {
      setFailure(t('commerce.checkout.choosePoint'));
      return;
    }
    setPlacing(true);
    setFailure(null);
    try {
      const byTransfer = payBy === 'BANK_TRANSFER' && Boolean(paymentOptions?.bankTransfer);
      const request: TransferCheckoutRequest = {
        cartToken: cart!.token,
        contactName: form.contactName.trim() || undefined,
        contactEmail: form.contactEmail.trim() || undefined,
        contactPhone: form.contactPhone.trim() || undefined,
        ...(collecting
          ? { fulfilmentType: 'COLLECTION', collectionLocationId: pointId!, billingAddress: address() }
          : { fulfilmentType: 'DELIVERY', shippingMethodId: methodId ?? undefined, deliveryAddress: address() }),
        customerPoNumber: showPoNumber ? form.customerPoNumber.trim() || undefined : undefined,
        note: showNote ? form.note.trim() || undefined : undefined,
        // Only ever sent when chosen: a backend that predates transfers then
        // sees exactly the request it always did.
        ...(byTransfer ? { paymentMethod: 'BANK_TRANSFER' as const } : {}),
      };
      const order = await commerce!.checkout!(request);
      const proof = {
        contactEmail: form.contactEmail.trim(),
        deliveryPostcode: form.postcode.trim(),
      };
      rememberPlacedOrder(order, proof);
      if (signedIn && saveToBook && picked === null && commerce!.saveAccountAddress) {
        // After the order, never before it: an address book that cannot be written must not cost the sale.
        const typed = address();
        await commerce!
          .saveAccountAddress({
            recipientName: typed.recipientName ?? '',
            line1: typed.line1 ?? '',
            line2: typed.line2,
            locality: typed.locality,
            region: typed.region,
            postcode: typed.postcode,
            countryCode: typed.countryCode ?? '',
            phone: typed.phone,
          })
          .catch(() => undefined);
      }
      // The basket is consumed by a successful checkout — its token is dead
      // upstream — so it is dropped here before leaving the page. Forgetting
      // after the navigation would race it.
      cartState!.forget();

      // Pay. The order EXISTS by this point, so a payment failure must not look
      // like a checkout failure: the shopper is sent to the confirmation page
      // either way, and that page shows the payment state and offers a retry.
      // Returning them to a checkout form whose basket no longer exists would
      // be the worst of both — it reads as "nothing happened" when in fact they
      // owe money for goods they have ordered.
      // A transfer is paid later, from the shopper's own bank: the confirmation
      // page shows them where to send it and which reference to quote.
      if (commerce!.payForOrder && !byTransfer) {
        try {
          const payment = await commerce!.payForOrder({
            orderNumber: order.orderNumber,
            ...proof,
            returnUrl: successHref,
            instrumentToken: instrument ?? undefined,
          });
          if (payment.actionUrl) {
            // 3-D Secure, or a hosted page. The acquirer brings them back to
            // `successHref`, where the confirmation block polls for the result.
            window.location.assign(payment.actionUrl);
            return;
          }
        } catch {
          // Swallowed on purpose — see above. The confirmation page reports the
          // unpaid state from the order itself, which is the authority anyway.
        }
      }

      // A full navigation, not a client-side push: everything on the next page
      // is about an order rather than a basket, and a hard load guarantees no
      // stale cart state survives into it.
      window.location.assign(successHref);
    } catch (error) {
      setFailure(error instanceof Error ? error.message : t('commerce.checkout.failed'));
      setPlacing(false);
    }
  }

  return (
    <BlockRoot as="section">
      <div style={{ ...sectionInner, maxWidth: 1040 }}>
        <h1 {...part('title')} style={titleStyle}>
          {tx(title) || t('commerce.checkout.title')}
        </h1>

        {(failure || cartState.error) && (
          <p {...part('error')} role="alert" style={errorStyle}>
            {failure || cartState.error}
          </p>
        )}

        <form onSubmit={(event) => void place(event)} style={layoutStyle}>
          <div {...part('fields')} style={{ display: 'grid', gap: 32, minWidth: 0 }}>
            <Fieldset legend={t('commerce.checkout.contact')}>
              <Field
                id="checkout-name"
                label={t('commerce.checkout.name')}
                value={form.contactName}
                onChange={set('contactName')}
                autoComplete="name"
                required
              />
              <Field
                id="checkout-email"
                label={t('commerce.checkout.email')}
                value={form.contactEmail}
                onChange={set('contactEmail')}
                type="email"
                autoComplete="email"
                required
                // It is the proof they will need to find the order again, and
                // the only way the shop can reach them about it.
                help={t('commerce.checkout.emailHelp')}
              />
              <Field
                id="checkout-phone"
                label={t('commerce.checkout.phone')}
                value={form.contactPhone}
                onChange={set('contactPhone')}
                type="tel"
                autoComplete="tel"
              />
            </Fieldset>

            {points.length > 0 && (
              <Fieldset legend={t('commerce.checkout.receiveHeading')}>
                <ul style={methodListStyle}>
                  {(['DELIVERY', 'COLLECTION'] as const).map((choice) => (
                    <li key={choice}>
                      <label style={methodStyle}>
                        <input
                          type="radio"
                          name="fulfilmentMode"
                          value={choice}
                          checked={mode === choice}
                          disabled={busy || placing}
                          onChange={() => void chooseMode(choice)}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>
                            {choice === 'DELIVERY'
                              ? t('commerce.checkout.modeDelivery')
                              : t('commerce.checkout.modeCollect')}
                          </span>
                          <span style={mutedTextStyle}>
                            {choice === 'DELIVERY'
                              ? t('commerce.checkout.modeDeliveryHint')
                              : t('commerce.checkout.modeCollectHint', { count: points.length })}
                          </span>
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Fieldset>
            )}

            {collecting && (
              <Fieldset legend={t('commerce.checkout.collectFrom')}>
                <ul style={methodListStyle}>
                  {points.map((point) => {
                    const today = todaysHours(point);
                    return (
                      <li key={point.id}>
                        <label style={{ ...methodStyle, alignItems: 'flex-start' }}>
                          <input
                            type="radio"
                            name="collectionPoint"
                            value={point.id}
                            checked={pointId === point.id}
                            disabled={busy || placing}
                            onChange={() => void choosePoint(point.id)}
                            required
                            style={{ marginTop: 4 }}
                          />
                          <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 2 }}>
                            <span style={{ fontWeight: 600 }}>{point.name}</span>
                            <span style={mutedTextStyle}>{addressLine(point)}</span>
                            <span style={mutedTextStyle}>
                              {today
                                ? t('commerce.checkout.openToday', { hours: today })
                                : t('commerce.checkout.closedToday')}
                            </span>
                            {point.note && <span style={mutedTextStyle}>{point.note}</span>}
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </Fieldset>
            )}

            <Fieldset
              legend={
                collecting ? t('commerce.checkout.billingAddress') : t('commerce.checkout.deliveryAddress')
              }
            >
              {collecting && <p style={{ ...mutedTextStyle, margin: 0 }}>{t('commerce.checkout.billingHelp')}</p>}
              {savedAddresses.length > 0 && (
                <div style={fieldStyle}>
                  <label htmlFor="checkout-saved-address" style={labelStyle}>
                    {t('commerce.checkout.savedAddress')}
                  </label>
                  <select
                    id="checkout-saved-address"
                    value={pickedId == null ? '' : String(pickedId)}
                    onChange={(event) => pickSaved(event.target.value)}
                    disabled={placing}
                    style={inputStyle}
                  >
                    {savedAddresses.map((saved) => (
                      <option key={saved.id} value={String(saved.id)}>
                        {addressSummary(saved)}
                      </option>
                    ))}
                    <option value="">{t('commerce.checkout.newAddress')}</option>
                  </select>
                </div>
              )}
              <Field
                id="checkout-line1"
                label={t('commerce.checkout.line1')}
                value={form.line1}
                onChange={set('line1')}
                autoComplete="address-line1"
                required
              />
              <Field
                id="checkout-line2"
                label={t('commerce.checkout.line2')}
                value={form.line2}
                onChange={set('line2')}
                autoComplete="address-line2"
              />
              <Field
                id="checkout-locality"
                label={t('commerce.checkout.locality')}
                value={form.locality}
                onChange={set('locality')}
                autoComplete="address-level2"
                required
              />
              <Field
                id="checkout-region"
                label={t('commerce.checkout.region')}
                value={form.region}
                onChange={set('region')}
                autoComplete="address-level1"
              />
              <Field
                id="checkout-postcode"
                label={t('commerce.checkout.postcode')}
                value={form.postcode}
                onChange={set('postcode')}
                autoComplete="postal-code"
                required
                help={t('commerce.checkout.postcodeHelp')}
              />
              <Field
                id="checkout-country"
                label={t('commerce.checkout.country')}
                value={form.countryCode}
                onChange={set('countryCode')}
                autoComplete="country"
                maxLength={2}
                required
              />
              {signedIn && picked === null && commerce.saveAccountAddress && (
                <label style={termsStyle}>
                  <input
                    type="checkbox"
                    checked={saveToBook}
                    onChange={(event) => setSaveToBook(event.target.checked)}
                    disabled={placing}
                  />
                  <span>{t('commerce.checkout.saveToBook')}</span>
                </label>
              )}
            </Fieldset>

            {!collecting && options.length > 0 && (
              <Fieldset legend={t('commerce.checkout.deliveryMethod')}>
                <ul style={methodListStyle}>
                  {options.map((option) => (
                    <li key={option.id}>
                      <label style={methodStyle}>
                        <input
                          type="radio"
                          name="shippingMethod"
                          value={option.id}
                          checked={methodId === option.id}
                          disabled={busy || placing}
                          onChange={() => void chooseMethod(option.id)}
                          required
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>
                            {option.serviceName || option.carrier || t('commerce.checkout.delivery')}
                          </span>
                          {option.transitDays != null && (
                            <span style={mutedTextStyle}>
                              {t('commerce.checkout.transitDays', { count: option.transitDays })}
                            </span>
                          )}
                        </span>
                        <span style={{ fontWeight: 600 }}>{money(option.price)}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Fieldset>
            )}

            {paymentOptions?.bankTransfer ? (
              <Fieldset legend={t('commerce.checkout.payBy')}>
                <ul style={methodListStyle}>
                  {(['CARD', 'BANK_TRANSFER'] as const).map((choice) => (
                    <li key={choice}>
                      <label style={methodStyle}>
                        <input
                          type="radio"
                          name="payBy"
                          value={choice}
                          checked={payBy === choice}
                          disabled={placing}
                          onChange={() => setPayBy(choice)}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>
                          <span style={{ display: 'block', fontWeight: 600 }}>
                            {choice === 'CARD' ? t('commerce.checkout.payByCard') : t('commerce.checkout.payByTransfer')}
                          </span>
                          {choice === 'BANK_TRANSFER' && (
                            <span style={mutedTextStyle}>{t('commerce.checkout.payByTransferHelp')}</span>
                          )}
                        </span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Fieldset>
            ) : null}

            {payBy === 'CARD' && paymentOptions?.testInstruments?.length ? (
              <Fieldset legend={t('commerce.checkout.payment')}>
                {/* Said plainly, and not in small print. A shop that looks like
                    it is taking a card and is not is the one thing here a
                    shopper cannot detect for themselves. */}
                <p style={testNoticeStyle} role="note">
                  {t('commerce.checkout.testModeNotice')}
                </p>
                <ul style={methodListStyle}>
                  {paymentOptions.testInstruments.map((option) => (
                    <li key={option.token}>
                      <label style={methodStyle}>
                        <input
                          type="radio"
                          name="testInstrument"
                          value={option.token}
                          checked={instrument === option.token}
                          disabled={placing}
                          onChange={() => setInstrument(option.token)}
                        />
                        <span style={{ flex: 1, minWidth: 0 }}>{option.label}</span>
                      </label>
                    </li>
                  ))}
                </ul>
              </Fieldset>
            ) : null}

            {(showPoNumber || showNote) && (
              <Fieldset legend={t('commerce.checkout.extras')}>
                {showPoNumber && (
                  <Field
                    id="checkout-po"
                    label={t('commerce.checkout.poNumber')}
                    value={form.customerPoNumber}
                    onChange={set('customerPoNumber')}
                  />
                )}
                {showNote && (
                  <label style={fieldStyle}>
                    <span style={labelStyle}>
                      {collecting ? t('commerce.checkout.collectionNote') : t('commerce.checkout.note')}
                    </span>
                    <textarea
                      rows={3}
                      value={form.note}
                      onChange={(event) => set('note')(event.target.value)}
                      style={{ ...inputStyle, resize: 'vertical' }}
                    />
                  </label>
                )}
              </Fieldset>
            )}
          </div>

          <aside {...part('summary')} style={summaryStyle}>
            <h2 style={summaryTitleStyle}>{t('commerce.checkout.summary')}</h2>
            <ul style={summaryListStyle}>
              {lines.map((line) => (
                <li key={line.id} style={summaryLineStyle}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    {line.quantity} × {line.description}
                  </span>
                  <span>{money(line.lineTotal)}</span>
                </li>
              ))}
            </ul>
            <dl style={totalsStyle}>
              <Total label={t('commerce.cart.subtotal')} value={money(cart?.subtotal)} />
              {cart?.discountTotal && cart.discountTotal !== '0.00' && (
                <Total label={t('commerce.cart.discount')} value={`-${money(cart.discountTotal)}`} />
              )}
              {collecting ? (
                <Total label={t('commerce.checkout.collection')} value={t('commerce.checkout.free')} />
              ) : (
                <Total
                  label={t('commerce.cart.shipping')}
                  value={
                    methodId == null && options.length > 0
                      ? t('commerce.checkout.chooseMethod')
                      : money(cart?.shippingTotal)
                  }
                />
              )}
              <Total label={t('commerce.cart.tax')} value={money(cart?.taxTotal)} />
              <Total label={t('commerce.cart.total')} value={money(cart?.total)} emphasis />
            </dl>

            {termsHref && (
              <label style={termsStyle}>
                <input type="checkbox" required />
                <span>
                  {t('commerce.checkout.termsPrefix')}{' '}
                  <PortalLink href={termsHref}>{t('commerce.checkout.termsLink')}</PortalLink>
                </span>
              </label>
            )}

            <button type="submit" disabled={busy || placing} style={primaryButtonStyle}>
              {placing ? t('commerce.checkout.placing') : t('commerce.checkout.place')}
            </button>
            <PortalLink href={cartHref} style={backLinkStyle}>
              {t('commerce.checkout.backToBasket')}
            </PortalLink>
          </aside>
        </form>
      </div>
    </BlockRoot>
  );
}

function Fieldset({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset style={fieldsetStyle}>
      <legend style={legendStyle}>{legend}</legend>
      <div style={{ display: 'grid', gap: 12 }}>{children}</div>
    </fieldset>
  );
}

function Field({
  id,
  label,
  value,
  onChange,
  type = 'text',
  autoComplete,
  required,
  maxLength,
  help,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  autoComplete?: string;
  required?: boolean;
  maxLength?: number;
  help?: string;
}) {
  const helpId = help ? `${id}-help` : undefined;
  return (
    <div style={fieldStyle}>
      <label htmlFor={id} style={labelStyle}>
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        required={required}
        maxLength={maxLength}
        aria-describedby={helpId}
        style={inputStyle}
      />
      {help && (
        <span id={helpId} style={mutedTextStyle}>
          {help}
        </span>
      )}
    </div>
  );
}

function Total({ label, value, emphasis }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div style={totalRowStyle}>
      <dt style={{ margin: 0, fontWeight: emphasis ? 600 : 400 }}>{label}</dt>
      <dd style={{ margin: 0, fontWeight: emphasis ? 700 : 400 }}>{value}</dd>
    </div>
  );
}

const titleStyle: CSSProperties = { margin: '0 0 24px', fontSize: 'var(--font-size-3xl, 30px)' };
const errorStyle: CSSProperties = {
  margin: '0 0 16px',
  padding: '12px 16px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-danger-50, #fef2f2)',
  color: 'var(--color-danger-700, #b91c1c)',
};
const layoutStyle: CSSProperties = {
  display: 'grid',
  gap: 32,
  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
  alignItems: 'start',
};
const fieldsetStyle: CSSProperties = { margin: 0, padding: 0, border: 'none' };
const legendStyle: CSSProperties = { padding: 0, marginBottom: 12, fontSize: 18, fontWeight: 600 };
const fieldStyle: CSSProperties = { display: 'grid', gap: 4 };
const labelStyle: CSSProperties = { fontSize: 14, fontWeight: 500 };
const inputStyle: CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  border: '1px solid var(--color-border, #e5e7eb)',
  font: 'inherit',
};
const mutedTextStyle: CSSProperties = {
  fontSize: 13,
  color: 'var(--color-text-secondary, #6b7280)',
};
const methodListStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 };
const methodStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 14px',
  border: '1px solid var(--color-border, #e5e7eb)',
  borderRadius: 'var(--radius-md, 8px)',
  cursor: 'pointer',
};
const summaryStyle: CSSProperties = {
  display: 'grid',
  gap: 16,
  padding: 20,
  borderRadius: 'var(--radius-card, 12px)',
  background: 'var(--color-surface-muted, #f9fafb)',
  border: '1px solid var(--color-border, #e5e7eb)',
  position: 'sticky',
  top: 16,
};
const summaryTitleStyle: CSSProperties = { margin: 0, fontSize: 18, fontWeight: 600 };
const summaryListStyle: CSSProperties = { listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 };
const summaryLineStyle: CSSProperties = { display: 'flex', gap: 12, justifyContent: 'space-between', fontSize: 14 };
const totalsStyle: CSSProperties = {
  display: 'grid',
  gap: 8,
  margin: 0,
  paddingTop: 16,
  borderTop: '1px solid var(--color-border, #e5e7eb)',
};
const totalRowStyle: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: 16 };
const testNoticeStyle: CSSProperties = {
  margin: 0,
  padding: '10px 12px',
  borderRadius: 'var(--radius-sm, 6px)',
  background: 'var(--color-warning-50, #fffbeb)',
  color: 'var(--color-warning-800, #92400e)',
  fontSize: 14,
};
const termsStyle: CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14 };
const primaryButtonStyle: CSSProperties = {
  padding: '14px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-primary, #2563eb)',
  color: 'var(--color-on-primary, #fff)',
  fontWeight: 600,
  border: 'none',
  cursor: 'pointer',
  font: 'inherit',
  fontSize: 16,
};
const secondaryButtonStyle: CSSProperties = {
  display: 'inline-block',
  padding: '12px 24px',
  borderRadius: 'var(--radius-md, 8px)',
  background: 'var(--color-surface-muted, #f3f4f6)',
  color: 'var(--color-text, #111827)',
  textDecoration: 'none',
  border: '1px solid var(--color-border, #e5e7eb)',
};
const backLinkStyle: CSSProperties = {
  textAlign: 'center',
  fontSize: 14,
  color: 'var(--color-text-secondary, #6b7280)',
};
